import { Navigate, useLocation } from "react-router-dom";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Stack } from "@/core/components/ui/stack";
import { isProductEnabled } from "@/business/product-access";
import { useGetBusiness } from "@/business/query";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import {
  hasAiScriptAccess,
  hasIcmPayrollViewerAccess,
  hasPayrollScriptAccess,
} from "@/core/business/payroll-only";
import { useBusinessProducts } from "@/core/business/use-business-products";
import {
  canManageSimpleOnboarding,
  isSimpleOnboardingRequired,
  requiresSimpleOnboardingRedirect,
  requiresSubmittedRedirect,
  resumeOnboardingPath,
  SIMPLE_ONBOARDING_PATHS,
} from "@/onboarding/routes";
import {
  isPayBreakdownPath,
  PAY_BREAKDOWN_PATH,
} from "@/payroll/pay-breakdown/routes";
import { useCanViewPayrollRoster } from "@/payroll/roster/permissions";
import {
  isPayrollSettingsPath,
  isPayrollEditorPath,
  PAYROLL_SETTINGS_ROSTER_PATH,
} from "@/payroll/settings/routes";
import { getMarketingProduct } from "@/product-marketing/routes";
import {
  canAccessProfitability,
  isProfitabilityPath,
  canOpenProfitabilityReports,
  PROFITABILITY_PATH,
} from "@/profitability/routes";
import { useGetMyUser } from "@/user/query";
import { isTech, isApOnly, isAdminOrOwner } from "@/user/utils";

const ROUTES = {
  login: "/login",
  getVolca: "/get-volca",
  onboarding: SIMPLE_ONBOARDING_PATHS.root,
  onboardingDefault: SIMPLE_ONBOARDING_PATHS.welcome,
  waitingForSetup: "/waiting-for-setup",
  profile: "/profile",
  referrals: "/referrals",
  accountsPayable: "/accounts-payable",
  businessSettings: "/business-settings",
  payBreakdown: PAY_BREAKDOWN_PATH,
} as const;

const TECH_ALLOWED_PATH_PREFIXES: string[] = [ROUTES.profile];

export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();

  const { isAuthenticated, isLoading, token, logout } = useAuth();
  const { businessId } = useBusiness();
  const { settings, isLoading: isProductsLoading } = useBusinessProducts();
  const enableAp = isProductEnabled(settings, "accounts_payable");
  const enableReferrals = isProductEnabled(settings, "referrals");

  const { data: user, isLoading: isUserLoading } = useGetMyUser();
  const { data: business, isLoading: isBusinessLoading } =
    useGetBusiness(businessId);

  const { canViewRoster: canUseRoster, isLoading: isRosterAccessLoading } =
    useCanViewPayrollRoster(business, user);

  if (isAuthenticated && token && !isUserLoading && !user) {
    console.warn(
      "Detected corrupted auth state: token exists but no user data. Logging out...",
    );
    logout();
    return null;
  }

  if (
    isLoading ||
    isUserLoading ||
    isBusinessLoading ||
    isProductsLoading ||
    (isRosterAccessLoading &&
      (isPayrollSettingsPath(location.pathname) ||
        location.pathname.startsWith(ROUTES.businessSettings)))
  ) {
    return (
      <Stack fullWidth className="h-full" justify="center" align="center">
        <LoadingSpinner />
      </Stack>
    );
  }

  if (!isAuthenticated || !token) {
    return <Navigate to={ROUTES.login} state={{ from: location }} replace />;
  }

  // Apply this before onboarding and tenant-based permissions. AP-only users
  // can use their profile even when no business has AP enabled.
  if (isApOnly(user?.role)) {
    const isApRoute =
      location.pathname === ROUTES.accountsPayable ||
      location.pathname.startsWith(`${ROUTES.accountsPayable}/`);
    if (location.pathname === ROUTES.profile || (enableAp && isApRoute)) {
      return <>{children}</>;
    }
    return (
      <Navigate
        to={enableAp ? ROUTES.accountsPayable : ROUTES.profile}
        replace
      />
    );
  }
  // The editor renders its own access and recovery screen. Its API authorizes every request.
  if (isPayrollEditorPath(location.pathname)) return <>{children}</>;

  const hasPayrollScript = hasPayrollScriptAccess(business);
  const hasAiScript = hasAiScriptAccess(business);
  const hasIcmAccess = hasIcmPayrollViewerAccess(business);
  const isCurrentPayBreakdownRoute = isPayBreakdownPath(location.pathname);

  if (isSimpleOnboardingRequired(business?.onboarding_step)) {
    // A technician or rep cannot run setup, so they wait for someone who can rather
    // than being handed a form the API will refuse. `/waiting-for-setup` is routed
    // outside this guard, so this redirect settles.
    if (!canManageSimpleOnboarding(user?.role)) {
      return (
        <Navigate
          to={ROUTES.waitingForSetup}
          state={{ from: location }}
          replace
        />
      );
    }

    // Already sent for review: the submitted page is the only onboarding page then, so a
    // step page cannot be reached with nothing on it to say the work is already in.
    if (
      requiresSubmittedRedirect(business?.documents_ready_at, location.pathname)
    ) {
      return (
        <Navigate
          to={SIMPLE_ONBOARDING_PATHS.submitted}
          state={{ from: location }}
          replace
        />
      );
    }

    if (
      requiresSimpleOnboardingRedirect(
        business?.onboarding_step,
        location.pathname,
      )
    ) {
      // Signing back in lands on the first step they have not answered. Sending everyone
      // to the welcome screen made a customer walk their own answers again to reach the
      // one they stopped at.
      return (
        <Navigate
          to={
            business ? resumeOnboardingPath(business) : ROUTES.onboardingDefault
          }
          state={{ from: location }}
          replace
        />
      );
    }

    return <>{children}</>;
  }

  const marketingProduct = getMarketingProduct(location.pathname);
  if (
    marketingProduct &&
    isAdminOrOwner(user?.role) &&
    !isProductEnabled(settings, marketingProduct)
  ) {
    return <>{children}</>;
  }

  // The roster page owns its denied state so direct links keep their context.
  if (location.pathname.replace(/\/$/, "") === PAYROLL_SETTINGS_ROSTER_PATH)
    return <>{children}</>;

  if (isProfitabilityPath(location.pathname)) {
    if (!isTech(user?.role) || canAccessProfitability(user))
      return <>{children}</>;
  }

  if (isTech(user?.role)) {
    // A tech only has a statement screen when their business runs a published AI
    // script; otherwise they get the "download the app" screen.
    if (!hasAiScript && !isCurrentPayBreakdownRoute) {
      return (
        <Navigate to={ROUTES.getVolca} state={{ from: location }} replace />
      );
    }

    if (isPayrollSettingsPath(location.pathname) && canUseRoster)
      return <>{children}</>;

    const techDefaultRoute = ROUTES.payBreakdown;
    const isAllowedTechRoute =
      isCurrentPayBreakdownRoute ||
      TECH_ALLOWED_PATH_PREFIXES.some((route) =>
        location.pathname.startsWith(route),
      );

    if (location.pathname === "/") {
      return (
        <Navigate to={techDefaultRoute} state={{ from: location }} replace />
      );
    }

    if (!isAllowedTechRoute) {
      return (
        <Navigate to={techDefaultRoute} state={{ from: location }} replace />
      );
    }

    return <>{children}</>;
  }

  const hasPayBreakdownAccess = hasPayrollScript || hasIcmAccess || hasAiScript;
  const isApRoute = location.pathname.startsWith("/accounts-payable");
  const canReachPayrollSettings =
    isPayrollSettingsPath(location.pathname) && canUseRoster;
  const defaultRoute = hasPayBreakdownAccess
    ? ROUTES.payBreakdown
    : enableReferrals
      ? ROUTES.referrals
      : enableAp
        ? ROUTES.accountsPayable
        : canOpenProfitabilityReports(user, {
              ...business,
              enabled_products: settings,
            })
          ? PROFITABILITY_PATH
          : ROUTES.profile;

  if (
    !enableReferrals &&
    location.pathname.startsWith(ROUTES.businessSettings)
  ) {
    return (
      <Navigate
        to={canUseRoster ? PAYROLL_SETTINGS_ROSTER_PATH : defaultRoute}
        replace
      />
    );
  }

  if (location.pathname === "/") {
    return <Navigate to={defaultRoute} state={{ from: location }} replace />;
  }

  if (
    !enableReferrals &&
    location.pathname !== ROUTES.profile &&
    !location.pathname.startsWith(ROUTES.onboarding) &&
    !(enableAp && isApRoute) &&
    !(isCurrentPayBreakdownRoute && hasPayBreakdownAccess) &&
    !canReachPayrollSettings
  ) {
    return <Navigate to={defaultRoute} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
